import { Trans, useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { PerfectButtonHoverVariants, PerfectButtonSize } from '../../Common/Buttons/perfect-base-button/perfect-base-button.enums';
import Dialog from '../base-dialog/base-dialog.component';
import { LevelOfExperience } from '../../../modules/create-position/enums/create-position-experience-level.enums';
import STRING_KEYS from '../../../language/keys';
import { useUpdatePositionMutation } from '../../../store/rtk-query/apis/position/hooks/update-position.mutation-hook';
import { ANALYTICS_EVENTS, EVENT_GROUP } from '../../../consts/analytics';
import { useAnalyticsContext } from '../../../contexts/analytics/useAnalyticsContext';
import { positionQuery } from '../../../store/rtk-query/apis/position/position.toolkit-api';
import { DialogWidthSize } from '../base-dialog/base-dialog.enums';
import { CONFIG } from '../../../config';
import { CompanySizeTierEnum } from '../../../enums/company-size-tier.enum';
import { ExactLocationDto } from '../../../models/dto/location.dto';
import { PositionRunTriggerTypeEnum } from '../../../enums/position-run-trigger-type.enum';
import { EditPositionDialogProps } from './edit-position-dialog';
import {
  BodyContainer, DialogHeaderTitle, FooterContainer, FooterInfoText, HeaderContainer, InfoButton, SaveButton, SomethingElseDescription,
} from './edit-position-dialog.styled';
import { EditPositionPreferencesSection } from './edit-position-preferences-section/edit-position-preferences-section.component';
import { LevelOfExperienceSection } from './level-of-experience-section/level-of-experience-section.component';
import { CompanyBackgroundSection } from './company-background-section/company-background-section.component';
import { SkillsSection } from './skills-section/skills-section.component';
import { FieldsOfStudySection } from './fields-of-study-section/fields-of-study-section.component';
import { PositionTitleSection } from './position-title-section/position-title-section.component';
import { useEditPositionPreferencesFormik } from './edit-position-dialog.formik';
import { EditPositionPreferencesFormikKeys } from './edit-position-dialog.enums';
import { CompanySizeSection } from './company-size-section/company-size-section.component';
import { LocationSection } from './position-location-section/location-section.component';

const SUPPORT_MAIL_SUBJECT = 'I want to edit my Position';

export const EditPositionDialog = ({
  onCloseDialog,
  pageName,
  isDialogOpen,
  errorHandler,
  position,
}: EditPositionDialogProps): JSX.Element => {
  const { t: translate } = useTranslation();
  const { analyticsTrackEvent } = useAnalyticsContext();
  const [updatePosition, { isLoading: isEditPositionLoading, isSuccess: isEditPositionSuccess, isError: isEditPositionError }] = useUpdatePositionMutation();
  const [getPosition] = positionQuery.useLazyGetPositionByIdQuery();
  const [getPositions] = positionQuery.useLazyGetOpenPositionsQuery();
  const [isLoading, setIsLoading] = useState(false);

  const {
    jobTitle,
    locations,
    remotePosition,
    levelsOfExperience,
    companyBackground,
    companySizes,
    degrees,
    fieldsOfStudy,
    skills,
    setValueByKey,
    save,
    dirty,
    isValid,
  } = useEditPositionPreferencesFormik({ submitHandler: updatePosition, position, triggerType: PositionRunTriggerTypeEnum.ManualUpdate });

  const onEditPositionSuccess = async () => {
    await Promise.all([getPosition(position.id), getPositions()]);
    setIsLoading(false);
    onCloseDialog({ positionReFetched: true });
  };

  useEffect(() => {
    if (isEditPositionLoading) {
      setIsLoading(true);
    }
  }, [isEditPositionLoading]);

  useEffect(() => {
    if (isEditPositionSuccess) {
      onEditPositionSuccess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditPositionSuccess]);

  useEffect(() => {
    if (isEditPositionError) {
      errorHandler();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditPositionError]);

  const onSaveButtonClick = async () => {
    await save();

    const payload = {
      eventName: ANALYTICS_EVENTS.EDIT_POSITION_PREFERENCES_DIALOG_SAVE_BUTTON_CLICKED,
      eventGroup: EVENT_GROUP.CLICK,
      pageName,
      pagePositionId: position.id,
      pagePositionName: position.jobTitle,
      pagePositionStatus: position.status,
    };
    analyticsTrackEvent(payload);
  };

  return (
    <Dialog
      isOpen={ isDialogOpen }
      onClose={ () => onCloseDialog({ positionReFetched: false }) }
      widthSize={ DialogWidthSize.Large }
    >
      <HeaderContainer>
        <DialogHeaderTitle>Position preferences</DialogHeaderTitle>
      </HeaderContainer>
      <BodyContainer>
        <PositionTitleSection
          jobTitle={ jobTitle }
          changeHandler={ (changedData) => {
            setValueByKey(EditPositionPreferencesFormikKeys.JobTitle, changedData);
          } }
          completeHandler={ (changedData) => setValueByKey(EditPositionPreferencesFormikKeys.JobTitle, changedData) }
        />
        <LocationSection
          locations={ locations }
          remotePosition={ remotePosition }
          changeHandler={ (changedData: ExactLocationDto[]) => setValueByKey(EditPositionPreferencesFormikKeys.Locations, changedData) }
          remotePositionChangeHandler={ (isRemotePosition) => setValueByKey(EditPositionPreferencesFormikKeys.RemotePosition, isRemotePosition) }
        />
        <LevelOfExperienceSection
          levelsOfExperience={ levelsOfExperience }
          changeHandler={ (changedData: LevelOfExperience[]) => setValueByKey(EditPositionPreferencesFormikKeys.LevelsOfExperience, changedData) }
        />
        <CompanyBackgroundSection
          companyBackground={ companyBackground }
          changeHandler={ (changedData: PositionJobRequirementState[]) => {
            setValueByKey(EditPositionPreferencesFormikKeys.CompanyBackground, changedData);
          } }
        />
        <CompanySizeSection
          companySizes={ companySizes }
          changeHandler={ (changedData: CompanySizeTierEnum[]) => setValueByKey(EditPositionPreferencesFormikKeys.CompanySizes, changedData) }
        />
        <FieldsOfStudySection
          degrees={ degrees }
          fieldsOfStudy={ fieldsOfStudy }
          changeHandler={ (changedDegrees, changedFieldsOfStudy) => {
            setValueByKey(EditPositionPreferencesFormikKeys.Degrees, changedDegrees);
            setValueByKey(EditPositionPreferencesFormikKeys.FieldsOfStudy, changedFieldsOfStudy);
          } }
        />
        <SkillsSection
          skills={ skills }
          changeHandler={ (changedData: PositionJobRequirementState[]) => setValueByKey(EditPositionPreferencesFormikKeys.Skills, changedData) }
        />
        <EditPositionPreferencesSection
          emoji="💡"
          title={ translate(STRING_KEYS.EDIT_POSITION_PREFERENCES_DIALOG.SECTIONS.SOMETHING_ELSE) }
        >
          <SomethingElseDescription>
            <Trans i18nKey={ STRING_KEYS.EDIT_POSITION_PREFERENCES_DIALOG.SECTIONS.SOMETHING_ELSE_DESCRIPTION }>
              <InfoButton href={ `mailto:${CONFIG.SUPPORT_MAIL_ADDRESS}?bcc=${CONFIG.CS_MAIL_ADDRESS}&subject=${SUPPORT_MAIL_SUBJECT}` } />
            </Trans>
          </SomethingElseDescription>
        </EditPositionPreferencesSection>
      </BodyContainer>
      <FooterContainer>
        <FooterInfoText>
          { translate(STRING_KEYS.EDIT_POSITION_PREFERENCES_DIALOG.FOOTER_INFO_TEXT) }
        </FooterInfoText>
        <SaveButton
          hoverVariant={ PerfectButtonHoverVariants.Grow }
          size={ PerfectButtonSize.Large }
          onClick={ onSaveButtonClick }
          disabled={ !dirty || !isValid }
          isLoading={ isLoading }
        >
          { translate(STRING_KEYS.EDIT_POSITION_PREFERENCES_DIALOG.BUTTON_TEXT) }
        </SaveButton>
      </FooterContainer>
    </Dialog>
  );
};
